import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc, deleteDoc, updateDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { FaCrown, FaTrash, FaClock } from 'react-icons/fa';
import YapperModal from '../components/YapperModal';

function PendingActivationsView({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checked, setChecked] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null, showCancel: false });

  const showAlert = (title, message, type = 'info') => {
    setModal({ isOpen: true, title, message, type, onConfirm: () => setModal((m) => ({ ...m, isOpen: false })), showCancel: false });
  };
  const showConfirm = (title, message, onConfirmCallback, type = 'warning') => {
    setModal({
      isOpen: true, title, message, type,
      onConfirm: () => { setModal((m) => ({ ...m, isOpen: false })); if (onConfirmCallback) onConfirmCallback(); },
      showCancel: true,
    });
  };

  const loadItems = useCallback(async () => {
    try {
      const snap = await getDocs(query(collection(db, 'pendingActivations'), orderBy('createdAt', 'desc')));
      const rows = await Promise.all(
        snap.docs.map(async (d) => {
          const data = { id: d.id, ...d.data() };
          let username = 'Unknown';
          let email = 'N/A';
          let alreadyPremium = false;
          if (data.userId) {
            try {
              const uDoc = await getDoc(doc(db, 'users', data.userId));
              if (uDoc.exists()) {
                username = uDoc.data().username || 'Unknown';
                alreadyPremium = !!uDoc.data().isPremium;
              }
              const pDoc = await getDoc(doc(db, 'userPrivate', data.userId));
              if (pDoc.exists()) email = pDoc.data().email || 'N/A';
            } catch (e) { /* ignore per-row lookup errors */ }
          }
          return { ...data, username, email, alreadyPremium };
        })
      );
      setItems(rows);
    } catch (e) {
      console.error('Failed to load pending activations:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const adminDoc = await getDoc(doc(db, 'admins', userId));
        setIsAdmin(adminDoc.exists());
      } catch (e) {
        console.error('Error checking admin status:', e);
      } finally {
        setChecked(true);
      }
    };
    checkAdmin();
  }, [user]);

  useEffect(() => {
    if (isAdmin) loadItems();
    else if (checked) setLoading(false);
  }, [isAdmin, checked, loadItems]);

  const grantPremium = (item) => {
    if (!item.userId) {
      showAlert('Missing User', 'This record has no user ID, so it cannot be resolved automatically.', 'error');
      return;
    }
    showConfirm('Grant Premium', `Activate premium for ${item.username} (${item.email}) and clear this pending order?`, async () => {
      try {
        await updateDoc(doc(db, 'users', item.userId), {
          isPremium: true,
          premiumActivatedAt: serverTimestamp(),
          premiumActivatedBy: localStorage.getItem('userId') || 'admin',
          premiumSource: 'manual-recovery',
        });
        await deleteDoc(doc(db, 'pendingActivations', item.id));
        setItems((list) => list.filter((i) => i.id !== item.id));
        showAlert('Premium Activated', `${item.username} now has premium.`, 'success');
      } catch (e) {
        showAlert('Error', `Failed to activate premium: ${e.message}`, 'error');
      }
    }, 'warning');
  };

  const dismiss = (item) => {
    showConfirm('Dismiss', `Remove this pending order (${item.orderId}) without granting premium?`, async () => {
      try {
        await deleteDoc(doc(db, 'pendingActivations', item.id));
        setItems((list) => list.filter((i) => i.id !== item.id));
      } catch (e) {
        showAlert('Error', `Failed to dismiss: ${e.message}`, 'error');
      }
    }, 'danger');
  };

  const formatDate = (ts) => (ts && ts.toDate ? ts.toDate().toLocaleString() : '');

  if (checked && !isAdmin) {
    return (
      <div style={{ maxWidth: 700, margin: '3rem auto', padding: '0 1rem', textAlign: 'center', color: '#fff' }}>
        <h2>Access denied</h2>
        <p>This page is only available to admins.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 820, margin: '2rem auto', padding: '0 1rem' }}>
      <div
        style={{
          background: 'var(--surface-card, #fff)',
          border: '1px solid var(--border-subtle, rgba(0,0,0,0.1))',
          borderRadius: 'var(--radius-lg, 16px)',
          boxShadow: 'var(--shadow-md)',
          padding: '1.5rem',
          color: 'var(--text-body)',
        }}
      >
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', color: 'var(--text-strong)' }}>
          <FaClock /> Pending Activations
        </h1>
        <p style={{ color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
          Orders where a payment went through but premium didn't activate. Grant premium to resolve.
        </p>

        {loading ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : items.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No pending activations. 🎉</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {items.map((item) => (
              <div
                key={item.id}
                style={{
                  border: '1px solid var(--border-subtle, rgba(0,0,0,0.12))',
                  borderRadius: 12,
                  padding: '0.9rem 1rem',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: 'var(--text-strong)' }}>
                      {item.username}{' '}
                      {item.alreadyPremium && <span style={{ fontSize: '0.75rem', color: '#2e7d32' }}>(already premium)</span>}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--brand-600, var(--brand-500))', wordBreak: 'break-word' }}>{item.email}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', wordBreak: 'break-word' }}>Order: {item.orderId}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{formatDate(item.createdAt)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                      onClick={() => grantPremium(item)}
                      title="Grant premium & resolve"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer',
                        background: 'var(--brand-gradient, linear-gradient(90deg,#667eea,#764ba2))', color: '#fff',
                        border: 'none', borderRadius: 999, padding: '0.45rem 0.85rem', fontWeight: 700,
                      }}
                    >
                      <FaCrown /> Grant Premium
                    </button>
                    <button
                      onClick={() => dismiss(item)}
                      title="Dismiss"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontSize: '1.05rem' }}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <YapperModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        onCancel={modal.showCancel ? () => setModal((m) => ({ ...m, isOpen: false })) : null}
      />
    </div>
  );
}

export default PendingActivationsView;
